import React from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

/**
 * Tier Progress Panel Component
 * Displays the status and progress of all metadata collection tiers
 */
const TierProgressPanel = ({ tierStatus, loading = false, compact = false }) => {
  // Tier 6 (usage analysis) is intentionally absent: the collector is
  // not implemented (workers/unified_worker.py:493 returns False), so
  // its row would always show "No data" — misleading. Re-add when the
  // collector ships. See backend/core/billing/plans.py:184 for the
  // matching backend note.
  const tiers = [
    { key: 'tier1', name: 'Discovery', description: 'Table/view discovery', interval: '15 min', icon: ChartBarIcon },
    { key: 'tier2', name: 'Schema', description: 'Column metadata', interval: '1 hour', icon: ChartBarIcon },
    { key: 'tier3', name: 'Changes', description: 'Change detection', interval: '5 min', icon: ChartBarIcon },
    { key: 'tier4', name: 'Profiling', description: 'Data profiling', interval: 'Weekly', icon: ChartBarIcon },
    { key: 'tier5_lineage', name: 'Lineage', description: 'Data lineage discovery', interval: 'Daily', icon: ChartBarIcon }
  ];

  const getTierStatusInfo = (tier) => {
    if (!tierStatus || !tierStatus[tier.key]) {
      return {
        status: 'unknown',
        statusColor: 'bg-gray-200',
        statusText: 'No data',
        statusIcon: ClockIcon,
        lastRun: null,
        lastSuccess: null,
        metrics: {}
      };
    }

    const data = tierStatus[tier.key];

    // Determine status based on last job
    let status = 'success';
    let statusColor = 'bg-success';
    let statusText = 'Healthy';
    let statusIcon = CheckCircleIcon;

    if (data.last_job_status === 'failed') {
      status = 'error';
      statusColor = 'bg-error';
      statusText = 'Failed';
      statusIcon = ExclamationCircleIcon;
    } else if (data.last_job_status === 'running') {
      status = 'running';
      statusColor = 'bg-info';
      statusText = 'Running';
      statusIcon = ArrowPathIcon;
    } else if (!data.last_run_at) {
      status = 'pending';
      statusColor = 'bg-warning';
      statusText = 'Pending';
      statusIcon = ClockIcon;
    }

    return {
      status,
      statusColor,
      statusText,
      statusIcon,
      lastRun: data.last_run_at,
      lastSuccess: data.last_success_at,
      metrics: data.latest_metrics || {}
    };
  };

  const StatusIcon = ({ icon: Icon, className }) => (
    <Icon className={`w-5 h-5 ${className}`} />
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="h-6 bg-gray-200 rounded w-48 animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
        </div>

        <div className="space-y-4">
          {[...Array(6)].map((_, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded w-24 mb-2 animate-pulse"></div>
                  <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-20 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (compact) {
    // Compact view for embedding in other panels
    return (
      <div className="space-y-2">
        {tiers.slice(0, 3).map((tier) => {
          const info = getTierStatusInfo(tier);
          const StatusIconComponent = info.statusIcon;

          return (
            <div key={tier.key} className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <StatusIconComponent className={`w-4 h-4 ${
                  info.status === 'error' ? 'text-red-600' :
                  info.status === 'running' ? 'text-blue-600' :
                  info.status === 'pending' ? 'text-yellow-600' :
                  'text-green-600'
                }`} />
                <span className="text-sm font-medium">{tier.name}</span>
              </div>
              <span className="text-xs text-gray-500">
                {info.lastRun ? formatDistanceToNow(new Date(info.lastRun), { addSuffix: true }) : 'Never'}
              </span>
            </div>
          );
        })}
      </div>
    );
  }

  // Full view
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Tiered Metadata Collection</h3>
          <p className="text-sm text-gray-500 mt-1">
            {`Monitoring ${tierStatus?.selected_schemas?.length || 0} schema(s)`}
          </p>
        </div>
        {tierStatus?.summary && (
          <div className="text-right">
            <div className="text-sm text-gray-500">Total Jobs</div>
            <div className="text-2xl font-bold text-gray-900">{tierStatus.summary.total_jobs || 0}</div>
          </div>
        )}
      </div>

      {/* Tier Status List */}
      <div className="space-y-3">
        {tiers.map((tier) => {
          const info = getTierStatusInfo(tier);
          const StatusIconComponent = info.statusIcon;

          return (
            <div
              key={tier.key}
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
            >
              {/* Left side - Tier info */}
              <div className="flex items-center gap-4 flex-1">
                {/* Status indicator */}
                <div className={`w-10 h-10 rounded-lg ${info.statusColor} bg-opacity-10 flex items-center justify-center`}>
                  <StatusIconComponent className={`w-5 h-5 ${
                    info.status === 'error' ? 'text-red-700' :
                    info.status === 'running' ? 'text-blue-700' :
                    info.status === 'pending' ? 'text-yellow-700' :
                    'text-green-700'
                  }`} />
                </div>

                {/* Tier details */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-gray-900">{tier.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                      {tier.interval}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">{tier.description}</div>

                  {/* Last run info */}
                  {info.lastRun && (
                    <div className="text-xs text-gray-400 mt-1">
                      Last run: {formatDistanceToNow(new Date(info.lastRun), { addSuffix: true })}
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Status and metrics */}
              <div className="text-right">
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${info.statusColor} bg-opacity-10 ${
                  info.status === 'error' ? 'text-red-700' :
                  info.status === 'running' ? 'text-blue-700' :
                  info.status === 'pending' ? 'text-yellow-700' :
                  'text-green-700'
                }`}>
                  {info.statusText}
                </div>

                {/* Show key metrics */}
                {info.metrics.tables_discovered !== undefined && (
                  <div className="text-xs text-gray-500 mt-1">
                    {info.metrics.tables_discovered} tables
                  </div>
                )}
                {info.metrics.duration_seconds !== undefined && info.metrics.duration_seconds !== null && (
                  <div className="text-xs text-gray-500">
                    {info.metrics.duration_seconds.toFixed(1)}s
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      {tierStatus?.summary && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-success">
                {tierStatus.summary.successful_jobs || 0}
              </div>
              <div className="text-xs text-gray-500 mt-1">Successful</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-error">
                {tierStatus.summary.failed_jobs || 0}
              </div>
              <div className="text-xs text-gray-500 mt-1">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-info">
                {tierStatus.summary.tables_discovered || 0}
              </div>
              <div className="text-xs text-gray-500 mt-1">Tables</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierProgressPanel;
